import type {
  FacilityResidentsDto,
  GetFacilityResidentsRequestDto,
} from '@/shared/api/modules/facilities';
import { FilterSection } from '@/shared/components';
import { useTableSortingState } from '@/shared/lib/hooks';
import { Table, TableHeaderCell, Typography } from '@/shared/ui';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { type FC } from 'react';
import { useFacilityResidentsFiltersStore } from '../model/store';
import { FacilityResidentsFiltersForm } from './facility-residents-filters-form';

const columnHelper = createColumnHelper<FacilityResidentsDto>();

type FacilityResidentsTableProps = {
  facilityId: string;
};

const columns = [
  columnHelper.accessor('name', {
    enableSorting: true,
    header: 'Name',
    maxSize: 200,
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('flagged', {
    enableSorting: true,
    header: 'Flagged',
    cell: ({ getValue }) => (
      <Typography
        className={getValue() ? 'text-dust-red-7' : 'text-polar-green-7'}
      >
        {getValue() ? 'Yes' : 'No'}
      </Typography>
    ),

    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('activeAlerts', {
    enableSorting: true,
    header: 'Active Alerts',
    maxSize: 200,
    cell: ({ getValue }) => <Typography>{getValue() ?? 'N/A'}</Typography>,

    footer: (info) => info.column.id,
  }),

  columnHelper.accessor('createdAlerts', {
    enableSorting: true,
    header: 'Created Alerts',
    cell: ({ getValue }) => <Typography>{getValue() ?? 'N/A'}</Typography>,
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('daysSinceReview', {
    enableSorting: true,
    header: 'Days since review',
    maxSize: 200,
    cell: ({ getValue }) => <Typography>{getValue() ?? 'N/A'}</Typography>,
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('rate', {
    enableSorting: true,
    header: () => <TableHeaderCell title="Rate" subtitle="End of Period" />,
    maxSize: 200,
    cell: ({ getValue }) => {
      const rate = getValue();
      const computedRate =
        typeof rate === 'number' ? `$${rate.toFixed(2)}` : 'N/A';

      return <Typography>{computedRate}</Typography>;
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('revenue', {
    enableSorting: true,
    header: () => <TableHeaderCell subtitle="Sum" title="Total Revenue" />,
    maxSize: 200,
    cell: ({ getValue }) => {
      const revenue = getValue();
      const computedRevenue =
        typeof revenue === 'number' ? `$${revenue.toFixed(2)}` : 'N/A';
      return <Typography>{computedRevenue}</Typography>;
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('payerType', {
    enableSorting: true,
    header: 'Pay Type',
    maxSize: 200,
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('levelOfCare', {
    enableSorting: true,
    header: 'Level of Care',
    cell: ({ getValue }) => {
      return (
        <Typography as="span" className="truncate text-[15px]">
          {'Level ' + getValue()}
        </Typography>
      );
    },
  }),
  columnHelper.accessor('daysOnCareLevel', {
    enableSorting: true,
    minSize: 180,
    header: 'Days on Care Level',
    cell: ({ getValue }) => {
      return (
        <Typography as="span" className="truncate text-[15px]">
          {getValue()}
        </Typography>
      );
    },
  }),
  columnHelper.accessor('lengthOfStay', {
    enableSorting: true,
    header: 'Length of Stay',
    cell: ({ getValue }) => {
      return (
        <Typography as="span" className="truncate text-[15px]">
          {getValue()}
        </Typography>
      );
    },
  }),
] as ColumnDef<FacilityResidentsDto>[];

export const FacilityResidentsTable: FC<FacilityResidentsTableProps> = () => {
  const { sortingState, setSortingState } = useTableSortingState<
    NonNullable<GetFacilityResidentsRequestDto['sortBy']>
  >({
    sortBy: 'name',
    sortDirection: 'asc',
  });

  const {
    search,
    setFilters,
    filteredFacilityResidents,
    itemsCount,
    currentPage,
    flagged,
    activeAlerts,
    levelsOfCare,
    payerTypes,
  } = useFacilityResidentsFiltersStore();

  const getFiltersLabel = () => {
    const labels: string[] = [];

    if (flagged) labels.push('Flagged');
    if (activeAlerts?.length)
      labels.push(
        `Active Alerts: ${activeAlerts.map((a) => a.label).join(', ')}`
      );
    if (payerTypes?.length) labels.push(`Pay Type: ${payerTypes.join(', ')}`);
    if (levelsOfCare?.length)
      labels.push(`Level of Care: ${levelsOfCare.join(', ')}`);

    return labels.length > 0 ? labels.join('; ') : '';
  };

  return (
    <div>
      <FilterSection
        title="Residents"
        itemsCount={itemsCount}
        getFiltersLabel={getFiltersLabel}
        form={<FacilityResidentsFiltersForm />}
        search={{
          value: search,
          onChange: (search) => {
            setFilters({ search, currentPage: 1 });
            useFacilityResidentsFiltersStore.getState().applyFilters();
          },
        }}
      />
      <Table
        striped
        firstColSticky
        headerSeparated
        columns={columns}
        sortingState={sortingState}
        data={filteredFacilityResidents}
        emptyState={!filteredFacilityResidents?.length}
        pagination={{
          currentPage,
          totalPages: 1,
          onPageChange: (page) => setFilters({ currentPage: page }),
        }}
        onSortingChange={setSortingState}
      />
    </div>
  );
};
